import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

export interface MenuNavigationOptions {
  currentPage?: string;
  onAuthenticationFailure?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class MenuNavigationService {
  private router = inject(Router);
  private authService = inject(AuthService);

  private readonly routeMap: Record<string, string> = {
    dashboard: '/dashboard',
    patients: '/patients',
    appointments: '/schedule/appointments',
    'manage-appointments': '/schedule/manage',
    schedule: '/schedule',
    requests: '/schedule/requests',
    reschedule: '/schedule/reschedule',
    'pending-approvals': '/pending-approvals',
    approvals: '/pending-approvals',
    users: '/admin/users',
    doctors: '/admin/doctors',
    nurses: '/admin/nurses',
    assistants: '/admin/assistants',
    'admin-assistants': '/admin/users/admin-assistants',
    billing: '/admin/billing',
    settings: '/admin/settings',
    pharmacy: '/pharmacy',
    'hospital-branches': '/admin/hospital-branches',
    'service-ticket': '/admin/service-ticket',
    'service-tickets': '/service-tickets',
    'create-ticket': '/service-tickets/create',
  };

  /**
   * Navigate to the appropriate page based on menu selection
   * @param menu Selected menu
   * @param options Navigation options
   * @returns true if navigation occurred, false otherwise
   */
  navigateToMenu(menu: string, options: MenuNavigationOptions = {}): boolean {
    if (!this.isAuthenticated()) {
      if (options.onAuthenticationFailure) {
        options.onAuthenticationFailure();
      } else {
        this.router.navigate(['/login']);
      }
      return false;
    }

    if (options.currentPage && options.currentPage === menu) {
      return false;
    }

    const route = this.routeMap[menu];

    if (route) {
      this.router.navigate([route]);
      return true;
    } else {
      this.router.navigate(['/dashboard']);
      return true;
    }
  }

  /**
   * Check if user is authenticated
   * @returns true if authenticated, false otherwise
   */
  private isAuthenticated(): boolean {
    if (!this.authService.isAuthenticated()) {
      return false;
    }

    const token = this.authService.getToken();
    if (!token) {
      localStorage.removeItem('current_user');
      localStorage.removeItem('auth_token');
      return false;
    }

    return true;
  }

  /**
   * Get route for a given menu
   * @param menu Menu name
   * @returns Route or null if not found
   */
  getRouteForMenu(menu: string): string | null {
    return this.routeMap[menu] || null;
  }

  /**
   * Add or update a route in the mapping
   * @param menu Menu name
   * @param route Route path
   */
  setRouteForMenu(menu: string, route: string): void {
    this.routeMap[menu] = route;
  }

  /**
   * Get all route mappings
   * @returns Complete mapping object
   */
  getAllRoutes(): Record<string, string> {
    return { ...this.routeMap };
  }
}
